<script lang="ts">
	import type { ISbStoryData } from '@storyblok/js';
	import { editable } from '../../editable';
	import Blok from '$lib/storyblok/blok.svelte';
	import type { SbImage, StripeProductData } from '$lib/storyblok/types';
	import PageGrid16 from '$lib/storyblok/components/pageGrid16.svelte';
	import Image from '$lib/storyblok/components/image.svelte';
	import Button from '$lib/buttons/v3/button.svelte';
	import { shoppingCart } from '$lib/state/shoppingCart.svelte';
	import MillStone from '$lib/icons/millStone.svelte';
	import { storyblok } from '$lib/state/storyblok.svelte';

	interface Props {
		content: ISbStoryData<{
			product_bundle_id: string;
			bg_image?: SbImage;
			bg_image_desktop?: SbImage;
			top_image?: SbImage;
			top_image_desktop?: SbImage;
			product: string;
			subheadline?: string;
			cta_label: string;
		}>['content'];
	}

	let { content }: Props = $props();

	let productBundle = $state<{ products: Array<{ stripe_product: StripeProductData }> }>({
		products: []
	});

	async function getProductBundle(bundleId: string) {
		const { data } = await storyblok.client.getStory<{
			products: Array<{ stripe_product: StripeProductData }>;
		}>(`cart/products/${bundleId}`);

		productBundle = data.story.content;
	}

	$effect(() => {
		getProductBundle(content.product_bundle_id);
	});
</script>

<div
	use:editable={content}
	class="product-selector py-12"
	style={`--bg-image: url(${content.bg_image?.filename}); --bg-image-desktop: url(${content.bg_image_desktop?.filename});`}
>
	<PageGrid16 margin>
		<section
			class="col-span-full overflow-hidden rounded-xl bg-white p-5 lg:col-start-3 lg:col-end-[15] lg:py-3 lg:pl-3 lg:pr-12"
		>
			{#if content.top_image}
				<Image
					class="aspect-video w-full rounded-lg object-cover object-top lg:hidden"
					src={content.top_image.filename}
					alt={content.top_image.alt}
					title={content.top_image.title}
				/>
			{/if}

			<div class="lg:flex lg:gap-x-12">
				{#if content.top_image_desktop}
					<Image
						class="flex-1 rounded-xl object-cover max-lg:hidden"
						src={content.top_image_desktop.filename}
						alt={content.top_image_desktop.alt}
						title={content.top_image_desktop.title}
					/>
				{/if}

				<div class="flex-1 lg:flex lg:flex-col lg:justify-center">
					<div class="mb-5 mt-4">
						<h1
							class="text-[24px]/[108%] font-semibold tracking-[-.24px] text-brown-1 lg:text-[32px]/[110%] lg:tracking-[-.32px]"
						>
							{content.product}
						</h1>
						{#if content.subheadline}
							<p
								class="mt-1 text-[16px]/[150%] tracking-[-.16px] text-brown-4 lg:text-[20px]/[140%] lg:tracking-[-.2px]"
							>
								{content.subheadline}
							</p>
						{/if}
					</div>

					{#if productBundle.products.length > 0}
						<div class="flex flex-col gap-y-3">
							{#each productBundle.products as product}
								<Blok content={product} />
							{/each}
						</div>
					{:else}
						<div class="flex h-full min-h-52 items-center justify-center">
							<MillStone
								class="h-12 w-12 animate-[spin_3s_linear_infinite] text-brown-2 lg:h-16 lg:w-16"
							/>
						</div>
					{/if}

					<Button
						class="btn-large mt-5 max-lg:w-full lg:mt-7 lg:self-start"
						onclick={() => shoppingCart.open()}>{content.cta_label}</Button
					>
				</div>
			</div>
		</section>
	</PageGrid16>
</div>

<style lang="scss">
	.product-selector {
		background-image: var(--bg-image);
		background-repeat: no-repeat;
		background-size: cover;
	}

	@media (min-width: 1024px) {
		.product-selector {
			background-image: var(--bg-image-desktop);
		}
	}
</style>
